<template>
    <DCard padding>
        <DRow>
            <DRow
                class="mb-3"
                align-horizontal="start"
                align-vertical="end"
            >
                <DCol
                    sm="3"
                    class="pt-1"
                >
                    <DInput
                        id="search"
                        v-model="filterParams.search"
                        :label="__('messages.search')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="changePage(1)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DCol>
                <DTable
                    class="table-responsive"
                    :sorter="isSortable"
                    :sorter-value="sorterValue"
                    :items="translations.data"
                    :fields="fields"
                    hover
                    @update:sorter-value="updateOrdering"
                >
                    <template #actions="{item}">
                        <td class="text-right">
                            <DButton
                                v-if="userPermissions.includes('setting.translations.edit')"
                                icon="pencil"
                                mini
                                @click="editItem(item.id)"
                            />
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="translations.last_page > 1"
                    :active-page="translations.current_page"
                    :pages="translations.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <UploadFileModal
            :show="showImportModal"
            :errors="importCsvErrors"
            @close="onCsvImportModalClose"
        />
        <DFabs>
            <DFab
                v-if="userPermissions.includes('setting.translations.edit')"
                icon="download"
                :label="__('settings.translation.export')"
                @click="exportCsv"
            />

            <DFab
                v-if="userPermissions.includes('setting.translations.edit')"
                icon="upload"
                :label="__('settings.translation.import')"
                @click="importCsv"
            />
        </DFabs>
    </DCard>
</template>

<script setup>

import { computed, ref, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import UploadFileModal from '~@/Components/Settings/TenantTranslations/UploadFileModal.vue'
import _ from 'lodash'

const props = defineProps({
    translations: {
        type: Object,
        default: () => {
            return {}
        }
    },
    languages: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    errors: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const showImportModal = ref(false)

const filterParams = {
    search: props.filters.search,
    order_by: props.filters.order_by,
    order_by_asc: props.filters.order_by_asc ? 1 : 0
}

const fields = [
    { key: 'key', label: 'Key', sorter: true },
    ...props.languages.map(language => ({
        ...language,
        _style: {},
        sorter: false
    })),
    { key: 'actions', sorter: false, label: '', _style: { minWidth: '5%' } }
]

const userPermissions = computed(() => usePage().props.auth.permissions)
const sorterValue = computed(() => {
    return {
        column: filterParams.order_by,
        asc: !!props.filters.order_by_asc
    }
})

const importCsvErrors = ref(props.errors)

watch(() => props.errors, (newErrors, oldErrors) => {
    importCsvErrors.value = _.pickBy(newErrors, function (value, key) {
        return _.startsWith(key, 'importCsv')
    })
})

const isSortable = computed(() => {
    if (!sorterValue.value.column) return false

    return { external: true }
})

const editItem = (id) => router.get(route('settings.tenant-translations.edit', { tenant_translation: id }))

const changePage = (page) => {
    const queryParameters = Object.fromEntries(Object.entries(filterParams).filter(([_, v]) => v))

    if (page > 1) {
        queryParameters.page = page
    }

    router.get(route('settings.tenant-translations.index', {
        save_filters: true
    }), queryParameters)
}
function resetFilters () {
    router.get(route('settings.tenant-translations.index', {
        page: props.translations.current_page,
        save_filters: true,
        default_filters: true
    }))
}
function updateOrdering (event) {
    filterParams.order_by = event.column
    filterParams.order_by_asc = event.asc ? 1 : 0

    changePage(props.translations.current_page)
}

function exportCsv () {
    window.open(route('settings.tenant-translations.export'), '_blank')
}
function importCsv () {
    showImportModal.value = true
}

function onCsvImportModalClose () {
    showImportModal.value = false
    importCsvErrors.value = {}
}

</script>

<style scoped lang="scss">

</style>
